import { Component, Input } from '@angular/core';
import { LocalizeService } from '../services/localize.service';
import { Util, UserInterface } from '../utils/utils.module';
import { ListItem } from '../models/list-item';
import { ListData } from '../models/base';
import { ListService } from '../services/list.service';
import { AppComponent } from '../app.component';

@Component({
  selector: 'edx-hero-tile',
  styleUrls: ['hero-tile-component.scss'],
  templateUrl: 'hero-tile-component.html',
  standalone: false
})
export class HeroTileComponent {
  private app: AppComponent;
  public ui: UserInterface = Util.Device.ui;
  public reloadStr: string;
  public welcomeHeader: string;
  public userName: string;
  public userID: string;
  public userLibrary: string;
  public userGroup: string;
  public userIDGuide: string;
  public userLibraryGuide: string;
  public userGroupGuide: string;
  public recentButton: HeroButton;
  public checkedOutButton: HeroButton;
  public uploadButton: HeroButton;
  public pftaButton: HeroButton;
  public showPftaNotification = false;
  @Input() showDragOver = false;
  RestAPI = Util.RestAPI;

  constructor(protected listService: ListService, private localizer: LocalizeService) {
    this.app = Util.RestAPI.getAppComponent();
    this.userName = Util.RestAPI.getUserFullName();
    this.userID = Util.RestAPI.getUserID();
    this.userLibrary = Util.RestAPI.getPrimaryLibrary();
    this.userGroup = !!Util.RestAPI.getLoginReply()['GROUP'] ? Util.RestAPI.getLoginReply()['GROUP'] : Util.RestAPI.getLoginReply()['GROUPS'] && Util.RestAPI.getLoginReply()['GROUPS'].length ? Util.RestAPI.getLoginReply()['GROUPS'][0]['GROUP_ID'] : '';
    this.welcomeHeader = this.localizer.getTranslation('HERO.WELCOME');
    this.userIDGuide = this.localizer.getTranslation('FORMS.LOCAL.AUTHENTICATE.USERID');
    this.userLibraryGuide = this.localizer.getTranslation('NAVBAR.PRIMARY');
    this.userGroupGuide = this.localizer.getTranslation('NAVBAR.PRIMARY_GROUP');
    this.reloadStr = this.localizer.getTranslation('HERO.RELOAD_TILES');
    this.reloadList();
    if (Util.RestAPI.canUserCreateDocument()) {
      this.uploadButton = {
        label: '',
        cmd: 'upload',
        header: this.localizer.getTranslation('HERO.SECTIONS.UPLOAD_FILES')
      };
    }
    if (this.ui === 0 && Util.RestAPI.siteConfigurations.showPftaNotification) {
      this.showPftaNotification = true;
      this.updatePftaNotification();
      Util.RestAPI.pftaNotificationEmitter.on('update', () => {
        this.updatePftaNotification();
      });
    }
  }

  public reloadList(): void {
    // fetch the last edited DOCUMENT only
    Util.RestAPI.get('folders', 'recentedits', 'max=1&descending=%RECENTACTIVITYDATE&filter=ITEM_TYPE=D and STORAGE=A,T,K and STATUS=0%7C3').subscribe((listData: ListData) => {
      Util.RestAPI.get('folders', 'checkedout', 'start=0&max=0&descending=LAST_EDIT_DATE').subscribe(async (checkedOutData: ListData) => {
        const failedItemCount = await Util.RestAPI.setFailedCheckins(listData);
        const desc: ListItem = (listData.list?.length ? listData.list[0] : null);
        if (desc) {
          this.recentButton = {
            label: desc.DOCNAME,
            icon: Util.Transforms.iconUrlFromDesc(desc),
            cmd: 'lastopened',
            value: +desc.id,
            desc,
            header: this.localizer.getTranslation('HERO.SECTIONS.RECENT')
          };
        }
        this.checkedOutButton = {
          label: checkedOutData.set.total > 0
            ? this.localizer.getTranslation('HERO.SECTIONS.ITEMS_COUNT', [checkedOutData.set.total])
            : this.localizer.getTranslation('HERO.SECTIONS.NO_ITEMS'),
          icon: 'assets/images/title_checked_out36.svg',
          cmd: 'checkedout',
          value: checkedOutData.set.total,
          header: this.localizer.getTranslation('HERO.SECTIONS.CHECKED_OUT'),
          tooltip: failedItemCount ? this.localizer.getTranslation('HERO.SECTIONS.ITEMS_WARNING', [failedItemCount.toString()]) : '',
          failedWarningIcon: failedItemCount ? 'assets/images/title_checked_out_warning.svg' : ''
        };
      });
    });
  }

  private updatePftaNotification(): void {
    const hasPFTA = Util.RestAPI.hasPFTA();
    this.pftaButton = {
      label: hasPFTA ? this.localizer.getTranslation('HERO.SECTIONS.PFTA_CONNECTED')
        : this.localizer.getTranslation('HERO.SECTIONS.PFTA_DISCONNECTED'),
      icon: hasPFTA ? 'assets/images/notification_success.svg'
        : 'assets/images/status_abort.svg',
      header: this.localizer.getTranslation('FORMS.LOCAL.PFTA.PFTA'),
    };
  }

  private refresh(): void {
    Util.RestAPI.reloadTiles();
  }

  private setupButtons(buttons: HeroButton[]) {
    for (const button of buttons) {
      if (button.icon) {
        button.icon = 'assets/images/' + button.icon;
      }
      if (button.label) {
        button.label = this.localizer.getTranslation(button.label);
      }
    }
  }

  private doButton(cmd: string, desc?: ListItem, event?: Event): void {
    if (!!event && event.type === 'keydown') {
      event.preventDefault();
    }
    switch (cmd) {
      case 'lastopened':
        this.openLastEditedItem(desc);
        break;
      case 'checkedout':
        this.app.doCommand('checkedout');
        break;
      case 'upload':
        Util.RestAPI.updateFilesDragDrop(true);
        Util.RestAPI.setLastCmdName(null);
        this.app.doCommand('newdocument');
        break;
    }
  }

  private openLastEditedItem(listItem: ListItem): void {
    if (listItem) {
      if (Util.isContainer(listItem.type)) {
        this.listService.openItem(listItem);
      } else {
        Util.RestAPI.viewOrOpenFilesWithStatusCheck([listItem], undefined, false, null);
      }
    }
  }

  private iconAltTextFromDesc(item: ListItem): string {
    return Util.Transforms.iconAltTextFromDesc(item);
  }
}

class HeroButton {
  label: string;
  icon?: string;
  value?: number;
  cmd?: string;
  desc?: ListItem;
  header?: string;
  tooltip?: string;
  failedWarningIcon?: string;
}
